import React, { useMemo, useState } from 'react';
import { ArrowRightIcon, MailIcon, PlayIcon, QuoteIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Waveform } from '../components/archive/AudioTile';
import { ResendLink } from '../components/auth/ResendLink';
import { YearsDemo } from '../components/landing/YearsDemo';
import { ARCHIVE_ITEMS, archiveStats } from '../data/archive';
import type { ArchiveItem } from '../data/archive';
import { parseYears, scaleMembers } from '../data/community';
import type { RegistrationDraft } from '../types/registration';
interface JoinProps {
  onStart: () => void;
  resumeStep: number;
  totalSteps: number;
  completed: RegistrationDraft | null;
  onStartOver: () => void;
  onEnterPortal: () => void;
}

/** One measure for the whole page. Nothing is full-bleed. */
const PAGE = 'mx-auto w-full max-w-[64rem] px-4 sm:px-6';

/** Small caps label above a zone — the page's only ornament. */
function Eyebrow({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="block font-mono text-eyebrow-caption uppercase tracking-[0.18em] text-ink-muted" data-id="element-941" data-mp="1b4fhdd93he5x">
      {children}
    </span>;
}

/** Label left, plain description right. Used by both list zones. */
function Row({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <li className="grid gap-1 border-b border-rule py-4 sm:grid-cols-[14rem_minmax(0,1fr)] sm:gap-8" data-id="element-942" data-mp="1ovapuofolt8z">
      <span className="text-body font-semibold text-ink" data-id="element-943" data-mp="1tt2ixls2bler">{label}</span>
      <span className="text-body text-ink-muted" data-id="element-944" data-mp="1ysymxl0rfcub">{children}</span>
    </li>;
}
const MEMBER_ACTIONS = [{
  label: 'Browse the full archive by year',
  body: 'Every photograph, recording, newsletter and memory from 1976 on.'
}, {
  label: 'Find people you knew',
  body: 'Search the directory by name, decade or connection.'
}, {
  label: 'Add your own material',
  body: 'Upload a photograph, write a memory, or identify people. Staff review everything before it appears.'
}, {
  label: 'Get the monthly letter',
  body: 'A short round-up of what was added, sent on the first of the month.'
}];
const PRIVACY = [{
  label: 'Your name',
  body: 'Members see your first name and last initial. Never your surname.'
}, {
  label: 'Your contact details',
  body: 'Your email and phone are never shown to another member.'
}, {
  label: 'Who can see you',
  body: 'You choose, before we ask about your time here.'
}, {
  label: 'What you contribute',
  body: 'Read by a staff member before it appears.'
}, {
  label: 'Search engines',
  body: 'Nothing here is indexed.'
}];

/** Prefer items inside the span, fall back to the rest of the archive. */
function within(items: ArchiveItem[], start: number, end: number, count: number): ArchiveItem[] {
  const inSpan = items.filter(item => item.year >= start && item.year <= end);
  const pool = inSpan.length >= count ? inSpan : [...inSpan, ...items.filter(item => !inSpan.includes(item))];
  return pool.slice(0, count).sort((a, b) => a.year - b.year);
}
export function Join({
  onStart,
  resumeStep,
  totalSteps,
  completed,
  onStartOver,
  onEnterPortal
}: JoinProps) {
  const [span, setSpan] = useState({
    start: 1994,
    end: 1999
  });
  const inProgress = resumeStep > 0 && !completed;
  const stats = archiveStats();
  const photographs = useMemo(() => ARCHIVE_ITEMS.filter(item => item.kind === 'photograph' && item.image), []);
  const hero = photographs[0];
  const plate = useMemo(() => within(photographs, span.start, span.end, 4), [photographs, span]);
  const recording = within(ARCHIVE_ITEMS.filter(item => item.kind === 'audio' && item.transcript), span.start, span.end, 1)[0];
  const memory = within(ARCHIVE_ITEMS.filter(item => item.kind === 'memory' && item.quote), span.start, span.end, 1)[0];
  const artifact = within(ARCHIVE_ITEMS.filter(item => item.kind === 'artifact' && item.image), span.start, span.end, 1)[0];
  const overlapping = useMemo(() => scaleMembers(false).filter(member => {
    const years = parseYears(member.years);
    return years.start <= span.end && years.end >= span.start;
  }).length, [span]);
  return <div className="flex flex-col bg-card" data-id="element-945" data-mp="1ikrca4kwro0y">
      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className={`${PAGE} py-12 lg:py-16`} data-id="element-946" data-mp="1dkwpac497il6">
        <div className="grid items-start gap-8 lg:grid-cols-2 lg:gap-12" data-id="element-947" data-mp="19lntl1pnlamz">
          <div className="flex flex-col gap-5" data-id="element-948" data-mp="14mkpbjhhz8g2">
            <Eyebrow data-id="element-949" data-mp="14kq3m2yejcax">1976–2026 · The Stepping Stone archive</Eyebrow>
            <h1 className="max-w-[19ch] text-display font-semibold leading-tight text-ink" data-id="element-950" data-mp="zmetrs83x1sp">
              Fifty years of Stepping Stone, kept by the people who were there.
            </h1>
            <p className="max-w-[46ch] text-body text-ink-muted" data-id="element-951" data-mp="2u1r6j5qtgcxm">
              Photographs, recordings, newsletters and written memories, arranged by the year they
              happened. Anyone connected to Stepping Stone can look through it and add to it.
            </p>

            {completed ? <div className="flex flex-col gap-3 rounded-card border border-rule bg-card p-5 shadow-card" data-id="element-952" data-mp="31k8lvsldilyi">
                <span className="flex items-center gap-2 text-heading font-semibold text-ink" data-id="element-953" data-mp="36b744xpx9u4e">
                  <MailIcon aria-hidden="true" className="h-4 w-4 text-primary-text" data-id="element-954" data-mp="3batff1m6x0ak" />
                  Check your email
                </span>
                <p className="text-small text-ink-muted" data-id="element-955" data-mp="2emdop27cgkvv">
                  We sent a link to{' '}
                  <span className="text-ink" data-id="element-956" data-mp="2inyxabcuid09">{completed.email.trim() || 'your inbox'}</span> to set
                  your password.
                </p>
                <div className="flex flex-wrap items-start gap-3 pt-1" data-id="element-957" data-mp="2skbzjxmghz6m">
                  <Button onClick={onEnterPortal} data-id="element-958" data-mp="2uqfji1bkfws">Go to the portal</Button>
                  <ResendLink email={completed.email.trim() || 'your inbox'} data-id="element-959" data-mp="2wuinxre9aw0" />
                  <Button variant="quiet" onClick={onStartOver} data-id="element-960" data-mp="7s9xarx87ath">
                    Walk through it again
                  </Button>
                </div>
              </div> : <div className="flex flex-wrap items-center gap-3" data-id="element-961" data-mp="39b77sij6vuoc">
                <Button size="lg" onClick={onStart} data-id="element-962" data-mp="3o9su2bnp38ko">
                  {inProgress ? 'Pick up where you left off' : 'Join the community'}
                </Button>
                {inProgress ? <Button variant="quiet" onClick={onStartOver} data-id="element-963" data-mp="2vk2edy6ezymg">
                    Start over
                  </Button> : null}
              </div>}
          </div>

          {hero?.image ? <figure className="flex flex-col gap-2" data-id="element-964" data-mp="35bgikj9b9mlx">
              <img src={hero.image.src} alt={hero.image.alt} className="aspect-[4/3] w-full rounded-card object-cover" data-id="element-965" data-mp="30jose9u2hiwb" />
              <figcaption className="font-mono text-eyebrow-caption uppercase tracking-[0.14em] text-ink-muted" data-id="element-966" data-mp="2crztgamqpw6e">
                {hero.year} · {hero.title}
              </figcaption>
            </figure> : null}
        </div>
      </section>

      {/* ── FROM THE ARCHIVE ─────────────────────────────────────────── */}
      <section aria-labelledby="taste" className={`${PAGE} border-t border-rule py-12`} data-id="element-967" data-mp="df2oxjj6p9t3">
        <h2 id="taste" className="sr-only" data-id="element-968" data-mp="idvsok8o5mg2">
          From the archive
        </h2>
        <Eyebrow data-id="element-969" data-mp="ibjaq72wvc30">From the archive</Eyebrow>

        <ul className="mt-6 grid grid-cols-2 gap-5 sm:grid-cols-4" data-id="element-970" data-mp="1636w69heluxw">
          {plate.map(photo => <li key={photo.id} className="flex flex-col gap-2" data-id="element-971" data-mp="1b1i4gfs1vxe0">
              <img src={photo.image!.src} alt={photo.image!.alt} className="aspect-[4/3] w-full rounded-field object-cover" data-id="element-972" data-mp="x2s5svpl1pnj" />
              <span className="font-mono text-eyebrow-caption tracking-[0.14em] text-ink-muted" data-id="element-973" data-mp="116803le6jdx2">
                {photo.year}
              </span>
            </li>)}
        </ul>

        <div className="mt-10 grid gap-4 border-t border-rule pt-10 lg:grid-cols-3" data-id="element-974" data-mp="3lshzuvl7lqoc">
          {recording ? <div className="flex flex-col gap-4 rounded-card bg-ink p-5" data-id="element-975" data-mp="cdoomsdpozvd">
              <span className="flex items-center justify-between font-mono text-eyebrow-caption uppercase tracking-[0.14em] text-on-ink opacity-70" data-id="element-976" data-mp="ccdl8to6hu4s">
                <span data-id="element-977" data-mp="3djvnn99m40q">{recording.year} · Recording</span>
                <span data-id="element-978" data-mp="3f3bxdux8f2i">{recording.duration}</span>
              </span>
              <span className="flex items-center gap-3 text-highlight" data-id="element-979" data-mp="101zl7jcdkddu">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-highlight" data-id="element-980" data-mp="w1fmibc5l1pl">
                  <PlayIcon aria-hidden="true" className="h-5 w-5 translate-x-0.5 text-on-highlight" data-id="element-981" data-mp="r9xmbphyf55o" />
                </span>
                <Waveform seed={recording.id} className="min-w-0 flex-1" data-id="element-982" data-mp="1jrrzw1q63qnk" />
              </span>
              <p className="text-body italic text-on-ink" data-id="element-983" data-mp="1f009br70hkxf">{recording.transcript}</p>
              <p className="text-small text-on-ink opacity-60" data-id="element-984" data-mp="1mby6kh661v7t">
                {recording.speaker}. Members hear the full {recording.duration}.
              </p>
            </div> : null}

          {memory ? <div className="flex flex-col gap-3 rounded-card border border-rule bg-card p-5 shadow-card" data-id="element-985" data-mp="1w9md04d4wr5t">
              <span className="font-mono text-eyebrow-caption uppercase tracking-[0.14em] text-ink-muted" data-id="element-986" data-mp="20cipddc2zr1b">
                {memory.year} · Memory
              </span>
              <QuoteIcon aria-hidden="true" className="h-5 w-5 text-ink-subtle" data-id="element-987" data-mp="2a9dnhv4fzpf2" />
              <p className="text-body italic text-ink" data-id="element-988" data-mp="2a7amyh5nydw3">“{memory.quote}”</p>
              <p className="text-small text-ink-muted" data-id="element-989" data-mp="2f0vm4ciztdwv">
                Written by a member. Attribution is shown to members only.
              </p>
            </div> : null}

          {artifact?.image ? <div className="flex flex-col gap-3 rounded-card border border-rule bg-card p-5 shadow-card" data-id="element-990" data-mp="2nyz1er3dxeg8">
              <span className="font-mono text-eyebrow-caption uppercase tracking-[0.14em] text-ink-muted" data-id="element-991" data-mp="2sxsb08fjwlb4">
                {artifact.year} · Artifact
              </span>
              <img src={artifact.image.src} alt={artifact.image.alt} className="h-40 w-full rounded-field bg-canvas object-contain p-2" data-id="element-992" data-mp="1iwdq8foucojh" />
              <p className="text-body font-medium text-ink" data-id="element-993" data-mp="1sn8cvl601q5g">{artifact.title}</p>
              <p className="text-small text-ink-muted" data-id="element-994" data-mp="1wmh34g1tz5w9">{artifact.note}</p>
            </div> : null}
        </div>
      </section>

      {/* ── FIND YOUR YEARS ──────────────────────────────────────────── */}
      <section aria-labelledby="years" className={`${PAGE} border-t border-rule py-12`} data-id="element-995" data-mp="26lzkqpu9qfl5">
        <Eyebrow data-id="element-996" data-mp="2bj824o7leflc">Find your years</Eyebrow>
        <h2 id="years" className="mt-3 text-section-heading font-semibold text-ink" data-id="element-997" data-mp="c7f4qqgw7883">
          Set the years you were here
        </h2>
        <p className="mt-2 max-w-[52ch] text-body text-ink-muted" data-id="element-998" data-mp="h5qhb8yknwu0">
          The archive and the directory are both arranged by year. One bar per member, placed by
          the years they were here.
        </p>

        <div className="mt-6" data-id="element-999" data-mp="2pp9swxe1q0wp">
          <YearsDemo start={span.start} end={span.end} onChange={setSpan} data-id="element-1000" data-mp="2pnyqp209117p" />
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-4" data-id="element-1001" data-mp="2lopuzrlnet9i">
          <p aria-live="polite" className="text-body text-ink" data-id="element-1002" data-mp="2gqg0zkbntyph">
            {overlapping > 0 ? <>
                <span className="font-semibold" data-id="element-1003" data-mp="3df447aipvheu">
                  {overlapping} {overlapping === 1 ? 'member was' : 'members were'} here
                </span>{' '}
                in {span.start}–{span.end}.
              </> : <>No members from {span.start}–{span.end} have joined yet.</>}
          </p>
          <Button variant="secondary" onClick={onStart} data-id="element-1004" data-mp="1sziyey5r8dqw">
            {overlapping > 0 ? 'Join to see them' : 'Join the community'}
          </Button>
        </div>
      </section>

      {/* ── WHAT MEMBERS CAN DO ──────────────────────────────────────── */}
      <section aria-labelledby="do" className={`${PAGE} border-t border-rule py-12`} data-id="element-1005" data-mp="1piej24rlzdp0">
        <h2 id="do" className="text-section-heading font-semibold text-ink" data-id="element-1006" data-mp="1ufoga6l8otne">
          What members can do
        </h2>
        <p className="mt-2 text-body text-ink-muted" data-id="element-1007" data-mp="1ggx4wrxrhm2y">Everything opens on the years you set.</p>

        <ul className="mt-6 border-t border-rule" data-id="element-1008" data-mp="1leghlnet9qss">
          {MEMBER_ACTIONS.map(action => <Row key={action.label} label={action.label} data-id="element-1009" data-mp="16q2erchea2c8">
              {action.body}
            </Row>)}
        </ul>
      </section>

      {/* ── PRIVACY ──────────────────────────────────────────────────── */}
      <section id="privacy" aria-labelledby="privacy-title" className={`${PAGE} border-t border-rule py-12`} data-id="element-1010" data-mp="xovwoyz3kjgv">
        <h2 id="privacy-title" className="text-section-heading font-semibold text-ink" data-id="element-1011" data-mp="ns9eyj22i8j2">
          Privacy
        </h2>
        <p className="mt-2 max-w-[52ch] text-body text-ink-muted" data-id="element-1012" data-mp="sqay13zhu7uj">
          Set before you are asked anything about your time here, and changeable at any point.
        </p>

        <ul className="mt-6 border-t border-rule" data-id="element-1013" data-mp="47ixjzm4m22s">
          {PRIVACY.map(item => <Row key={item.label} label={item.label} data-id="element-1014" data-mp="j4jer96oyen0">
              {item.body}
            </Row>)}
        </ul>
      </section>

      {/* ── JOIN ─────────────────────────────────────────────────────── */}
      <section className={`${PAGE} border-t border-rule py-12`} data-id="element-1015" data-mp="s3my8e9rsw21">
        <h2 className="text-section-heading font-semibold text-ink" data-id="element-1016" data-mp="n5lio3bjzevb">Join the community</h2>
        <p className="mt-2 max-w-[52ch] text-body text-ink-muted" data-id="element-1017" data-mp="11uhwkh0ajm3u">
          Free to join. Seven short questions, about three minutes. You can stop at any point.
        </p>
        <Button size="lg" className="mt-6" onClick={onStart} data-id="element-1018" data-mp="ww6ks0qfr1iz">
          {inProgress ? 'Pick up where you left off' : 'Join the community'}
          <span className="font-mono text-eyebrow-caption uppercase tracking-[0.14em] opacity-70" data-id="element-1019" data-mp="16tuj10n92r5w">
            3 min
          </span>
          <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-1020" data-mp="30bibnlwvd5sb" />
        </Button>
        <p className="mt-3 font-mono text-eyebrow-caption uppercase tracking-[0.14em] text-ink-muted" data-id="element-1021" data-mp="35amjo9824xh2">
          {stats.total} items · New material added weekly
        </p>
      </section>
    </div>;
}